import React from 'react'
import { Button, Input, Modal, Checkbox, Select, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import { withRouter } from 'react-router-dom'
import { auth } from '@services/api'
import { alertPopup } from '@utils/popup'
import { PurchaseStore } from 'store/purchaseStore'
import { buildFreePurchasePayload, normalizeProductPrice, handleDuplicatePaymentCheck } from '@utils/purchasePopupUtils'
import { isPrepaymentActive } from '@utils/prepaymentDateUtils'
import { resolveCardBlockedStatus } from '@utils/cardBlockedStatus'
import { PaymentListenerContent } from '@pages/PaymentListener'
import {
    sanitizeFullName,
    sanitizeEmail,
    sanitizeAddress,
    sanitizeNoNumbers,
    sanitizePhone,
    hasEnglishLetters,
    buildHebrewOnlyFullName,
    APOSTROPHE_REGEX
} from '@utils/tranzilaTextUtils'

const styles = `
    .ant-select-dropdown-menu {
        direction: ltr  !important;
    }
`

// Popup store contract used by this component.
type PopupStore = {
    popupType: string
    togglePopup: (value: boolean | string) => void
}

// Product data shape required to build payment params.
type ProductDetails = {
    id: number | string
    name: string
    sku: string
    price: number
    productType: string
    subscriptionKey: string
    iteration_type?: string
    iteration_charge?: string
}

// Client information required for prefill and payment submission.
export type ClientInfo = {
    id: number | string
    agent_id: number | string
    agent_name?: string
    subscription_status?: string | boolean | null
    first_name?: string
    last_name?: string
    phone?: string
    identity?: string
    email?: string
    company?: string
    city?: string
    address?: string
    country_code?: string
    prepayment_until?: string
    prepayment_since?: string
    prepayment_months?: number
    isPrepayment?: boolean
}

// Editable fields collected before iframe payment starts.
type ClientDetails = {
    fullName: string
    phone: string
    email: string
    company: string
    city: string
    address: string
}

// Payload shape passed to Tranzila URL builder.
type TranzilaClientPayload = {
    name: string
    company: string
    phone: string
    email?: string
    address: string
    city: string
    id: number | string
    countryPhoneCode?: string
}

// Component dependencies injected from parent/store context.
type TranzilaPopupProps = {
    popupStore: PopupStore
    productDetails: ProductDetails
    clientInfo: ClientInfo
    getClientInfo?: () => Promise<void> | void
    purchaseStore: PurchaseStore
    onPaymentSuccess?: () => void
}

// Local state for payment flow and form handling.
type TranzilaPopupState = {
    tranzilaPaymentStatus: boolean | null
    paymentConfirmationFromAPI: boolean | null
    paymentURL: string
    isIframeLoaded: boolean
    clientDetails: ClientDetails
    isTranzilaFormSubmitted: boolean
    isPaymentListenerVisible: boolean
    couponCode: string
    showCouponError: string
    isLoadingCoupon: boolean
    priceBeforeDiscount: number
    currentPrice: number
    defaultProductPrice: number
    activeSubscriptionKey: string | number
    isPrepayment: boolean
    prepaymentMonths: number | null
    isDuplicateCheckLoading: boolean
}

// Shared popup id used by button and modal checks.
const POPUP_TYPE = 'TranzilaPopup'
const PURCHASE_POPUP_TYPE = 'PurchasePopup'
const COUPON_VALIDATION_DELAY_MS = 300
const DEBUG_SUM_VALUE = '1'
const PAYMENT_GATEWAY = process.env.REACT_APP_PAYMENT_GATEWAY || ''

function isDev(href: string): boolean {
    try {
        const hostname = new URL(href).hostname.toLowerCase()
        return hostname === 'localhost' || hostname === '127.0.0.1' || PAYMENT_GATEWAY.includes('dev')
    } catch {
        return href.toLowerCase().includes('localhost') || PAYMENT_GATEWAY.includes('dev')
    }
}
// Hebrew UI copy extracted to centralize display strings.
const HEBREW_TEXTS = {
    buttonTitle: 'תשלום מאובטח',
    securePaymentTitle: 'טופס תשלום מאובטח',
    detailsTitle: 'מילוי פרטים',
    separator: '-',
    shekel: 'ש"ח',
    fullName: 'שם מלא',
    phone: 'טלפון',
    email: 'אימייל',
    company: 'חברה',
    city: 'עיר',
    address: 'כתובת',
    submit: 'שלח',
    price_after_discount: 'מחיר לאחר הנחה:',
    couponPlaceholder: 'קוד קופון',
    validateCoupon: 'בדוק קופון',
    couponError: 'קופון לא תקין',
    backToPurchase: 'חזרה למסך רכישה',
    prepayment: 'תשלום מראש',
    prepaymentMonths: 'מספר חודשים'
}

// Base form defaults to ensure stable controlled inputs.
const DEFAULT_CLIENT_DETAILS: ClientDetails = {
    fullName: '',
    phone: '',
    email: '',
    company: '',
    city: '',
    address: ''
}

// Normalize missing product price to zero for controlled state defaults.
function getBaseProductPrice(productDetails: ProductDetails): number {
    return normalizeProductPrice(productDetails.price)
}

function isFreePurchasePrice(price: unknown): boolean {
    return normalizeProductPrice(price) === 0
}

function normalizeSubscriptionKey(value: unknown): string | number {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : ''
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!trimmedValue) {
            return ''
        }

        const loweredValue = trimmedValue.toLowerCase()
        if (loweredValue === 'undefined' || loweredValue === 'null') {
            return ''
        }

        return trimmedValue
    }

    return ''
}

function resolveSubscriptionKey(activeSubscriptionKey: unknown, productSubscriptionKey: unknown): string | number {
    const normalizedActiveKey = normalizeSubscriptionKey(activeSubscriptionKey)
    if (normalizedActiveKey !== '') {
        return normalizedActiveKey
    }

    return normalizeSubscriptionKey(productSubscriptionKey)
}

function isPaymentListenerUrl(url: string): boolean {
    return url.indexOf('/payment-listener/') !== -1
}

// Keep popup-open checks readable when used in lifecycle methods.
function isPopupOpen(popupStore?: PopupStore): boolean {
    return !!(popupStore && popupStore.popupType === POPUP_TYPE)
}

// Keep store injection compatible with existing MobX decorator setup.
@inject((stores: any) => {
    const { popupStore, purchaseStore } = stores
    return { popupStore, purchaseStore }
})
@observer
class TranzilaPopup extends React.Component<TranzilaPopupProps, TranzilaPopupState> {
    // Iframe reference is reused for direct src updates.
    iframeRef = React.createRef<HTMLIFrameElement>()
    couponValidationTimeout: ReturnType<typeof setTimeout> | null = null
    couponValidationRequestId = 0
    lastKnownPopupOpen = false
    private pendingIframeURL: string | null = null
    private isUnmounted = false
    private hasSentPlusSubSms = false

    // Initialize default state for the payment modal lifecycle.
    constructor(props: TranzilaPopupProps) {
        super(props)

        // Keep initial UI and payment flow state deterministic between modal openings.
        this.state = {
            tranzilaPaymentStatus: null,
            paymentConfirmationFromAPI: null,
            paymentURL: '',
            isIframeLoaded: false,
            clientDetails: DEFAULT_CLIENT_DETAILS,
            isTranzilaFormSubmitted: false,
            isPaymentListenerVisible: false,
            couponCode: '',
            showCouponError: '',
            isLoadingCoupon: false,
            priceBeforeDiscount: 0,
            currentPrice: getBaseProductPrice(props.productDetails),
            defaultProductPrice: getBaseProductPrice(props.productDetails),
            activeSubscriptionKey: resolveSubscriptionKey('', props.productDetails.subscriptionKey),
            isPrepayment: false,
            prepaymentMonths: null,
            isDuplicateCheckLoading: false
        }
    }

    // Prevent setState on unmounted component to avoid React warnings.
    setState<K extends keyof TranzilaPopupState>(
        state:
            | TranzilaPopupState
            | ((
                  prevState: Readonly<TranzilaPopupState>,
                  props: Readonly<TranzilaPopupProps>
              ) => TranzilaPopupState | Pick<TranzilaPopupState, K> | null)
            | Pick<TranzilaPopupState, K>
            | null,
        callback?: () => void
    ): void {
        if (this.isUnmounted) {
            return
        }
        super.setState(state as any, callback)
    }

    // Clear pending coupon debounce timers to avoid overlapping requests.
    clearCouponValidationTimeout(): void {
        if (this.couponValidationTimeout) {
            clearTimeout(this.couponValidationTimeout)
            this.couponValidationTimeout = null
        }
    }

    // Keep stale responses from older requests from overwriting fresh state.
    isCouponRequestExpired(requestId: number): boolean {
        return requestId !== this.couponValidationRequestId
    }

    // Reset coupon-dependent prices when coupon field becomes empty.
    resetCouponStateToBasePrice(): void {
        const { defaultProductPrice } = this.state

        this.setState({
            isLoadingCoupon: false,
            priceBeforeDiscount: 0,
            currentPrice: defaultProductPrice
        })
    }

    // Clear coupon input and restore original price whenever modal flow is closed.
    resetCouponStateOnClose(): void {
        // Cancel pending coupon validations so stale responses cannot re-apply discount state.
        this.clearCouponValidationTimeout()
        this.couponValidationRequestId += 1
        this.resetCouponStateToBasePrice()

        this.setState({
            couponCode: '',
            showCouponError: '',
            isLoadingCoupon: false
        })
    }

    // Keep coupon error output consistent for both failed responses and exceptions.
    setCouponValidationErrorState(requestId: number): void {
        if (this.isCouponRequestExpired(requestId)) {
            return
        }

        // Invalid coupon should revert pricing to the original value.
        this.resetCouponStateToBasePrice()
        this.setState({ showCouponError: HEBREW_TEXTS.couponError, isLoadingCoupon: false })
    }

    // Apply successful coupon pricing while preserving existing debug override behavior.
    applyCouponValidationSuccess(afterDiscount: number, couponCode: string): void {
        const { defaultProductPrice } = this.state

        // Coupons that do not reduce price should restore the original price.
        if (afterDiscount >= defaultProductPrice) {
            this.setState({
                priceBeforeDiscount: 0,
                isLoadingCoupon: false,
                showCouponError: '',
                currentPrice: defaultProductPrice,
                couponCode
            })
            return
        }

        this.setState({
            priceBeforeDiscount: defaultProductPrice,
            isLoadingCoupon: false,
            showCouponError: '',
            currentPrice: afterDiscount,
            couponCode
        })
    }

    // Handle prepayment toggle and update pricing accordingly.
    handlePrepaymentToggle = (isPrepayment: boolean): void => {
        const { productDetails, purchaseStore } = this.props
        purchaseStore.setSelectedTranzilaProduct({
            ...productDetails,
            iteration_type: isPrepayment ? 'ONCE' : 'MONTHLY'
        })

        if (isPrepayment) {
            this.setState({ isPrepayment: true, prepaymentMonths: null })
        } else {
            this.setState({ isPrepayment: false })
        }
    }

    // Debounce coupon validation so we only call API after user pauses typing.
    handleCouponInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const couponCode = e.target.value

        this.setState({ couponCode, showCouponError: '' })
        this.clearCouponValidationTimeout()

        if (!couponCode.trim()) {
            this.resetCouponStateToBasePrice()
            return
        }

        this.couponValidationTimeout = setTimeout(() => {
            this.validateCoupon()
        }, COUPON_VALIDATION_DELAY_MS)
    }

    // Validate coupon against API and guard against stale asynchronous updates.
    validateCoupon = async (): Promise<void> => {
        const requestId = ++this.couponValidationRequestId
        const couponCode = this.state.couponCode.trim()
        const { productDetails, clientInfo } = this.props

        this.setState({ isLoadingCoupon: true, showCouponError: '' })

        try {
            const res = await auth.validateCouponCode({
                ['subscription_id']: productDetails.id,
                user_id: clientInfo.id,
                code: couponCode,
                special_sub_price: 0,
                fromAdminPanel: true,
                agent_id: clientInfo.agent_id
            })

            if (this.isCouponRequestExpired(requestId)) {
                return
            }

            if (res.status === 200) {
                const { afterDiscount } = res.data

                this.applyCouponValidationSuccess(afterDiscount, couponCode)
                return
            }

            this.setCouponValidationErrorState(requestId)
        } catch (error) {
            this.setCouponValidationErrorState(requestId)
        }
    }

    // Setup listeners and prefill values when the component mounts.
    async componentDidMount(): Promise<void> {
        const { clientInfo, popupStore, productDetails } = this.props
        this.lastKnownPopupOpen = isPopupOpen(popupStore)
        // Save the client refresh function.
        this.props.purchaseStore.setClientInfoFetcher(this.props.getClientInfo)

        // Start listening for postMessage callbacks from the payment listener flow.
        window.addEventListener('message', this.receiveMessage, false)

        // Prefill with existing client data so submission can happen immediately.
        this.setState({ clientDetails: this.createClientDetails(clientInfo) })

        // Keep the original mount-time initialization, but avoid firing the free purchase side effect here.
        if (!isPopupOpen(popupStore)) {
            return
        }

        // Pre-build the Tranzila iframe URL only for paid products; free ones are handled on submit.
        if (!isFreePurchasePrice(productDetails.price)) {
            this.initPaymentURL()
        }
    }

    // React to popup visibility transitions and trigger iframe readiness.
    componentDidUpdate(prevProps: TranzilaPopupProps): void {
        const productChanged =
            prevProps.productDetails && this.props.productDetails
                ? prevProps.productDetails.id !== this.props.productDetails.id
                : false
        const { clientInfo } = this.props

        // When a different product loads, reset pricing to the new base values.
        // Keep the discounted price intact for the same product (e.g., after applying a coupon).
        if (productChanged) {
            const basePrice = getBaseProductPrice(this.props.productDetails)
            this.setState({
                currentPrice: basePrice,
                defaultProductPrice: basePrice,
                activeSubscriptionKey: resolveSubscriptionKey('', this.props.productDetails.subscriptionKey)
            })
        }

        const popupIsOpen = isPopupOpen(this.props.popupStore)

        // If the modal just closed, clear transient iframe/coupon state so next open starts fresh.
        // CRITICAL: Prepayment MUST be unchecked and months MUST be null on every modal open (unless forced).
        if (this.lastKnownPopupOpen && !popupIsOpen) {
            this.setState({
                isIframeLoaded: false,
                clientDetails: this.createClientDetails(this.props.clientInfo),
                isPrepayment: false,
                prepaymentMonths: null,
                isDuplicateCheckLoading: false
            })
            this.hasSentPlusSubSms = false
            this.resetCouponStateOnClose()
        }

        // If the modal just opened, run the duplicate payment check before showing the form.
        // Skip the check when the client has an active prepayment (prepayment_until is today or future).
        if (!this.lastKnownPopupOpen && popupIsOpen) {
            const { clientInfo, productDetails } = this.props
            if (productDetails && productDetails.id && !isPrepaymentActive(clientInfo.prepayment_until)) {
                this.setState({ isDuplicateCheckLoading: true })
                handleDuplicatePaymentCheck(auth, clientInfo.id, productDetails.id, () => {
                    this.setState({ isDuplicateCheckLoading: false })
                }).then(() => {
                    if (!this.isUnmounted) {
                        this.setState({ isDuplicateCheckLoading: false })
                    }
                })
            }
        }

        // If the modal just opened and we don't yet have a payment URL, build it now.
        if (!this.lastKnownPopupOpen && popupIsOpen && !this.state.paymentURL) {
            this.initPaymentURL()
        }

        // If we already have a URL (e.g., from submit) and the modal just reopened, load it right away.
        if (
            !this.lastKnownPopupOpen &&
            popupIsOpen &&
            this.state.paymentURL &&
            this.state.isTranzilaFormSubmitted &&
            this.iframeRef.current
        ) {
            this.setState({ isIframeLoaded: true })
            this.loadIframe(this.state.paymentURL)
        }

        this.lastKnownPopupOpen = popupIsOpen
    }

    componentWillUnmount(): void {
        this.isUnmounted = true
        // Guard against async callbacks calling setState after unmount.
        // @ts-ignore override instance method
        this.setState = () => undefined
        window.removeEventListener('message', this.receiveMessage, false)
        this.clearCouponValidationTimeout()
    }

    // Build a full name that skips any first/last part containing English letters.
    getHebrewOnlyFullName(clientInfo: ClientInfo): string {
        const firstName = (clientInfo.first_name || '').trim()
        const lastName = (clientInfo.last_name || '').trim()
        return buildHebrewOnlyFullName(firstName, lastName)
    }

    // Use typed full name only when it does not contain English letters.
    getSafeClientFullName(clientInfo: ClientInfo): string {
        const typedFullName = (this.state.clientDetails.fullName || '').trim()
        const hasEnglish = hasEnglishLetters(typedFullName)

        if (typedFullName && !hasEnglish) {
            return typedFullName
        }

        return this.getHebrewOnlyFullName(clientInfo)
    }

    // Normalize client data into the local editable form model.
    createClientDetails(clientInfo: ClientInfo): ClientDetails {
        // Preserve legacy phone normalization behavior used in this flow.
        const normalizedPhone = clientInfo.phone
            ? clientInfo.identity
                ? `0${clientInfo.phone}`
                : `${clientInfo.phone}`
            : ''

        // Return a fully-populated controlled object for input binding.
        return {
            fullName: this.getHebrewOnlyFullName(clientInfo),
            phone: normalizedPhone,
            email: clientInfo.email || '',
            company: clientInfo.company || '',
            city: clientInfo.city || '',
            address: clientInfo.address || ''
        }
    }

    // Resolve the payment endpoint based on product pricing mode.
    initPaymentURL(): void {
        const href = (window.location.href || '').toLowerCase()
        const { productDetails } = this.props
        const baseGateway = href.includes('stage') ? 'meditestst' : isDev(href) ? 'meditestdev' : 'mediwho1'
        const isProd = baseGateway === 'mediwho1'
        const currentGateway =baseGateway
        ///const currentGateway = productDetails.id === 189 && isProd ? 'ishpuzplus' : baseGateway
        // Paid products go through Tranzila hosted iframe endpoint.
        const paymentURL = `https://direct.tranzila.com/${currentGateway}/iframenew.php?`
        this.setState({ paymentURL })
    }

    // Capture iframe mount and lazily load payment data when ready.
    handleIframeRef = (element: HTMLIFrameElement | null): void => {
        this.iframeRef.current = element

        // Attach any deferred URL (like payment-listener) once the iframe actually mounts.
        if (element && this.pendingIframeURL) {
            element.src = this.pendingIframeURL
            this.pendingIframeURL = null
            return
        }

        // The iframe might mount after form submit, so ensure delayed load still runs.
        if (
            element &&
            !this.state.isIframeLoaded &&
            this.state.paymentURL &&
            this.isIframeReadyToLoad(this.state.paymentURL) &&
            this.state.isTranzilaFormSubmitted &&
            this.props.popupStore &&
            this.props.popupStore.popupType === POPUP_TYPE
        ) {
            this.setState({ isIframeLoaded: true }, () => {
                this.loadIframe(this.state.paymentURL)
            })
        }
    }

    // Route to the payment listener result page, updating the blocking state before navigation.
    async redirectToPaymentListener(isSuccess: boolean): Promise<void> {
        const url = `${window.location.origin}/payment-listener/${isSuccess ? 1 : 0}`

        await this.setStateAsync({
            tranzilaPaymentStatus: isSuccess,
            isPaymentListenerVisible: true,
            isTranzilaFormSubmitted: true,
            isIframeLoaded: true
        })

        await this.loadPaymentListenerInIframe(url)

        if (isSuccess) {
            const clientInfo = this.props.clientInfo

            if (this.props.getClientInfo) {
                try {
                    await this.props.getClientInfo()
                } catch (err) {
                    console.error('[TranzilaPopup] getClientInfo failed after payment', err)
                }
            }

            if (!this.hasSentPlusSubSms) {
                this.hasSentPlusSubSms = true
                try {
                    await auth.sendPlusSubSms({
                        client_id: clientInfo.id,
                        lng: 'he',
                        fromAdminPanel: true,
                        agent_id: clientInfo.agent_id
                    })
                } catch (err) {
                    console.error('[TranzilaPopup] sendPlusSubSms failed', err)
                }
            }

            try {
                await this.props.purchaseStore.updateAfterRelease()
            } catch (err) {
                console.error('[TranzilaPopup] updateAfterRelease failed', err)
            }

            if (this.props.onPaymentSuccess) {
                this.props.onPaymentSuccess()
            }
        }
    }

    private async loadPaymentListenerInIframe(url: string): Promise<void> {
        const iframe = this.iframeRef.current
        if (iframe) {
            iframe.src = url
            return
        }

        // Defer setting src until the iframe mounts.
        this.pendingIframeURL = url
    }

    // Detect when iframe has moved to payment-listener and adjust UI size.
    handleIframeLoad = (): void => {
        const iframe = this.iframeRef.current
        if (!iframe) {
            return
        }

        const source = iframe.src || iframe.getAttribute('src') || ''
        const isPaymentListenerVisible = source.indexOf('/payment-listener/') !== -1

        if (isPaymentListenerVisible !== this.state.isPaymentListenerVisible) {
            this.setState({ isPaymentListenerVisible })
        }
    }

    // Handle postMessage callbacks from the payment listener flow.
    receiveMessage = async (event: MessageEvent): Promise<void> => {
        // Ignore unrelated postMessage payloads from other sources.
        if (typeof event.data !== 'boolean') {
            return
        }

        try {
            // Boolean `true` means payment was approved by listener page.
            if (event.data) {
                await this.setStateAsync({ tranzilaPaymentStatus: true })
                await this.redirectToPaymentListener(true)
                return
            }

            // Boolean `false` keeps existing failure path and redirect behavior.
            await this.setStateAsync({ tranzilaPaymentStatus: false })
            await this.redirectToPaymentListener(false)
        } catch (err) {
            console.error('[TranzilaPopup] Failed to process payment result', err)
        }
    }

    // Update one field in the client details form state.
    setValue(value: string, field: keyof ClientDetails): void {
        // Update only one controlled field while preserving the rest.
        this.setState(prevState => ({
            clientDetails: {
                ...prevState.clientDetails,
                [field]: value
            }
        }))
    }

    // Create an onChange handler that applies sanitization to a specific field.
    createSanitizedInputHandler(sanitizer: (value: string) => string, field: keyof ClientDetails) {
        return (e: React.ChangeEvent<HTMLInputElement>) => {
            this.setValue(sanitizer(e.target.value), field)
        }
    }

    private setStateAsync(stateUpdate: Partial<TranzilaPopupState>): Promise<void> {
        return new Promise(resolve => this.setState(stateUpdate as TranzilaPopupState, () => resolve()))
    }

    private async shouldReleaseBlockedCard(): Promise<boolean> {
        const { cardStatus, recentCardBlockedRow } = await this.props.purchaseStore.checkCreditCardStatus(
            this.props.clientInfo
        )

        return resolveCardBlockedStatus(cardStatus, recentCardBlockedRow).cardCurrentlyBlocked
    }

    // Build Tranzila query params from product and client payloads.
    async buildTranzilaParams(
        productDetails: ProductDetails,
        clientDetails: TranzilaClientPayload
    ): Promise<URLSearchParams> {
        const { clientInfo } = this.props
        const { iteration_type, iteration_charge, id, sku, name, productType } = productDetails
        const subscriptionKey = resolveSubscriptionKey(this.state.activeSubscriptionKey, productDetails.subscriptionKey)
        const { name: clientName, phone, email, id: clientId, countryPhoneCode, address, city, company } = clientDetails
        const shouldReleaseBlockedCard = await this.shouldReleaseBlockedCard()

        // Coerce both sides to Number before comparing to avoid string/number identity mismatch (e.g. 5 !== "5")
        const providedBy = Number(clientId) !== Number(clientInfo.agent_id) ? clientInfo.agent_id : null
        const transactionData = iteration_charge || '4_approved'
        // Do NOT pre-encode values passed to URLSearchParams — it encodes them again, producing %2520 instead of %20
        const description = `${sku} - ${name ? name.replace(APOSTROPHE_REGEX, '') : ''}`
        const safeClientName = clientName ? clientName.replace(APOSTROPHE_REGEX, '') : ''
        const trimmedClientName = safeClientName.length > 19 ? `${safeClientName.substring(0, 15)}...` : safeClientName
        // Strip leading 0 from local phone when prepending a country code (e.g. +972 + 0541234567 → +972541234567)
        const localPhone = countryPhoneCode ? phone.replace(/^0/, '') : phone
        const fullPhone = countryPhoneCode ? `${countryPhoneCode}${localPhone}` : localPhone
        const contact = `${trimmedClientName} ${clientId}`
        const isDevStageLocal = isDev(window.location.href) || window.location.href.includes('admin-stage')

        // Build gateway payload as query params to preserve historical behavior.
        const params = new URLSearchParams()
        params.set('nologo', '1')
        params.set('trTextColor', '2A2A2A')
        params.set('trButtonColor', '000000')
        params.set('trButtonHoverColor', '000000')
        params.set('lang', 'il')
        params.set('currency', '1')

        if (iteration_type === 'MONTHLY' && !this.state.isPrepayment) {
            // Recurring metadata is sent only for monthly products (not for one-time prepayment).
            params.set('recur_transaction', transactionData)
        }

        params.set('product_id', String(id))
        params.set('email', String(email))
        params.set('pdesc', description)
        params.set('user_id', String(clientId))

        if (providedBy) {
            // Provider id is optional and only sent when resolved.
            params.set('provided_by', String(providedBy))
        }
        params.set('contact', contact)
        params.set('phone', fullPhone)
        params.set('address', address ? address.replace(APOSTROPHE_REGEX, '') : '')
        params.set('city', city ? city.replace(APOSTROPHE_REGEX, '') : '')
        params.set('subscriptionKey', String(subscriptionKey))

        // Calculate sum: prepayment uses price × months, regular uses currentPrice
        const sumAmount =
            this.state.isPrepayment && this.state.prepaymentMonths
                ? Number((this.state.currentPrice * this.state.prepaymentMonths).toFixed(2))
                : Number(this.state.currentPrice.toFixed(2))
        params.set('sum', isDevStageLocal ? DEBUG_SUM_VALUE : String(sumAmount))

        if (this.state.isPrepayment) {
            params.set('isPrepayment', '1')
            if (this.state.prepaymentMonths) {
                params.set('prepayment_months', String(this.state.prepaymentMonths))
            }
        }
        if (this.state.isPrepayment) {
            params.set('payment_type', 'once')
        } else {
            params.set('payment_type', productType)
        }
        params.set('shouldReleaseBlockedCard', shouldReleaseBlockedCard ? '1' : '0')

        if (company) {
            // Company value is optional in gateway schema.
            params.set('company', company)
        }

        params.set('tranmode', 'A')
        params.set('client_internal_id', encodeURI(String(clientId)))

        return params
    }

    // Load the iframe source with the computed payment URL and params.
    async loadIframe(url: string): Promise<void> {
        const { clientInfo, productDetails } = this.props

        // Guard against calling before required client data exists.
        if (!clientInfo) {
            return
        }

        // Use submitted values first, fallback to client record values.
        const clientDetails: TranzilaClientPayload = {
            name: this.getSafeClientFullName(clientInfo),
            company: this.state.clientDetails.company || '',
            phone:
                this.state.clientDetails.phone ||
                (clientInfo.identity ? `0${clientInfo.phone || ''}` : clientInfo.phone || ''),
            // Explicit '' fallback prevents String(undefined) = 'undefined' being sent to Tranzila
            email: this.state.clientDetails.email || clientInfo.email || '',
            address: this.state.clientDetails.address || '',
            city: this.state.clientDetails.city || '',
            id: clientInfo.id,
            countryPhoneCode: clientInfo.country_code
        }

        const iframe = this.iframeRef.current
        if (!iframe) {
            return
        }

        if (url.indexOf('/payment-listener/') !== -1) {
            iframe.src = url
            return
        }

        const missingFields = this.validateTranzilaParams(productDetails, clientDetails)
        if (missingFields.length > 0) {
            alertPopup({
                type: 'error',
                title: 'שגיאה בנתוני תשלום',
                body: `חסרים פרטים נדרשים לביצוע התשלום: ${missingFields.join(', ')}`
            })
            return
        }

        const params = await this.buildTranzilaParams(productDetails, clientDetails)
        const baseUrl = url.includes('?') ? url : `${url}?`

        if (this.isUnmounted || !this.iframeRef.current) {
            return
        }

        this.iframeRef.current.src = baseUrl + params.toString()
    }

    // Returns the names of any required Tranzila payment fields that are missing or empty.
    private validateTranzilaParams(productDetails: ProductDetails, clientDetails: TranzilaClientPayload): string[] {
        const subscriptionKey = resolveSubscriptionKey(this.state.activeSubscriptionKey, productDetails.subscriptionKey)

        const missing: string[] = []
        const normalizedProductType = String(productDetails.productType || '')
            .trim()
            .toLowerCase()

        if (productDetails.id === undefined || productDetails.id === null || productDetails.id === '') {
            missing.push('מזהה מוצר')
        }
        if (!productDetails.sku) {
            missing.push('SKU מוצר')
        }
        if (!productDetails.name) {
            missing.push('שם מוצר')
        }
        if (normalizedProductType !== 'monthly') {
            missing.push('סוג מוצר')
        }
        if (subscriptionKey === '') {
            missing.push('מפתח מנוי')
        }
        if (!Number.isFinite(this.state.currentPrice) || this.state.currentPrice < 0) {
            missing.push('מחיר')
        }
        if (clientDetails.id === undefined || clientDetails.id === null || clientDetails.id === '') {
            missing.push('מזהה לקוח')
        }
        if (!clientDetails.name) {
            missing.push('שם לקוח')
        }
        if (!clientDetails.phone) {
            missing.push('טלפון')
        }
        if (!clientDetails.email) {
            missing.push('אימייל')
        }
        if (this.state.isPrepayment && this.state.prepaymentMonths === null) {
            missing.push('מספר חודשים לתשלום מראש')
        }

        return missing
    }

    // Close the popup and clear transient payment state.
    onClose = (): void => {
        this.props.popupStore.togglePopup(false)
    }

    // Return control to the purchase popup without wiping in-progress selections.
    goBackToPurchase = (): void => {
        this.props.popupStore.togglePopup(PURCHASE_POPUP_TYPE)
    }

    // Reset transient iframe state once the modal finishes closing to keep the iframe visible during the animation.
    handleAfterClose = (): void => {
        this.setState({ isIframeLoaded: false, isTranzilaFormSubmitted: false, isPaymentListenerVisible: false })
        // Clear collective flag so a later normal purchase isn't treated as collective.
        this.props.purchaseStore.setCollectiveMode(false)
        // Don't reset PurchasePopup when the user clicked "back to purchase" — they are actively using it.
        const userWentBackToPurchase = this.props.popupStore.popupType === PURCHASE_POPUP_TYPE
        const shouldResetSelectedProduct =
            !userWentBackToPurchase &&
            (this.state.isTranzilaFormSubmitted ||
                this.state.isPaymentListenerVisible ||
                this.state.tranzilaPaymentStatus !== null)

        if (shouldResetSelectedProduct) {
            this.props.purchaseStore.setSelectedTranzilaProduct()
        }
    }

    // Build base API payload shared between blocked and purchase submissions.
    private buildPayloadData(blockedStatus: boolean): Record<string, any> {
        return {
            user_id: this.props.clientInfo.id,
            subscription_id: this.props.productDetails.id,
            price: this.state.currentPrice,
            code: this.state.couponCode,
            referrer: null,
            psoriasis: null,
            fromAdminPanel: true,
            agent_id: this.props.clientInfo.agent_id,
            blockedStatus
        }
    }

    // Summary confirm (with agent name), then create the ACTIVE collective sub — no charge.
    confirmCollective = (): void => {
        const { clientInfo, productDetails, getClientInfo } = this.props
        Modal.confirm({
            title: 'שיוך לקולקטיב',
            content: (
                <div style={{ direction: 'rtl', textAlign: 'right' }}>
                    <p>
                        <strong>סוכן: </strong>
                        {clientInfo.agent_name || ''}
                    </p>
                    <p>
                        <strong>מוצר: </strong>
                        {productDetails ? productDetails.name : ''}
                    </p>
                </div>
            ),
            okText: 'אישור',
            cancelText: 'ביטול',
            onOk: async () => {
                try {
                    const { fullName, phone, email, company, city, address } = this.state.clientDetails
                    await auth.createCollectiveSubscription({
                        user_id: clientInfo.id,
                        subscription_id: productDetails.id,
                        email,
                        city,
                        address
                    })
                    alertPopup({ type: 'success', title: 'נוסף לקולקטיב בהצלחה' })
                    this.onClose()
                    if (getClientInfo) await getClientInfo()
                } catch (e) {
                    alertPopup({ type: 'error', title: 'שגיאה בשיוך לקולקטיב', body: '' })
                }
            }
        })
    }

    // Validate form data and kick off payment iframe submission.
    submitTranzilaForm = async (): Promise<void> => {
        const { fullName, phone, email, city, address } = this.state.clientDetails

        if (!this.props.purchaseStore.cardStatusResolved) {
            return
        }

        // Prevent submit until all required fields are populated.
        if (!fullName || !phone || !email || !city || !address) {
            return
        }

        // Collective flow: no charge. Show a summary (with agent name), then add to collective.
        if (this.props.purchaseStore.collectiveMode) {
            this.confirmCollective()
            return
        }

        // Re-check for duplicate payment on submit before proceeding.
        // Skip the check when the client has an active prepayment (prepayment_until is today or future).
        const { clientInfo, productDetails } = this.props
        if (productDetails && productDetails.id && !isPrepaymentActive(clientInfo.prepayment_until)) {
            let canProceed = false
            await handleDuplicatePaymentCheck(auth, clientInfo.id, productDetails.id, () => {
                canProceed = true
            })
            if (!canProceed) {
                return
            }
        }

        const recentBlockedRow = this.props.purchaseStore.getRecentCardBlockedRow()

        // Add a new blocked row when the selected product differs from the current blocked row.
        if (this.shouldCreateNewBlockedRow(recentBlockedRow)) {
            try {
                await auth.createSubscriptionRegistry(this.buildPayloadData(true))
            } catch (error) {
                alertPopup({
                    type: 'error',
                    title: 'שגיאה',
                    body: 'יצירת רישום מנוי נכשלה'
                })
                return
            }
        }

        const apiData = this.buildPayloadData(false)
        const isFree = isFreePurchasePrice(this.state.currentPrice)

        let subscriptionKey: string | number
        try {
            subscriptionKey = await this.createRegistryAndResolveKey(apiData, isFree ? 'free purchase' : 'paid')
        } catch (error) {
            console.error('Failed to create subscription registry:', error)
            alertPopup({
                type: 'error',
                title: 'שגיאה',
                body: 'יצירת רישום מנוי נכשלה'
            })
            return
        }

        // Free products bypass Tranzila entirely and jump straight to the payment listener.
        if (isFree) {
            await this.handleFreePurchaseSubmission(subscriptionKey)
            return
        }

        // Payment URL and iframe source must be refreshed with the submitted user details.
        // Reset isPaymentListenerVisible to false so loader appears after submit
        this.setState(
            {
                isTranzilaFormSubmitted: true,
                isIframeLoaded: false,
                isPaymentListenerVisible: false,
                activeSubscriptionKey: ''
            },
            () => this.handlePaidPurchaseSubmission(subscriptionKey)
        )
    }

    private loadPaymentIframeIfReady(): void {
        // Load the iframe only when both the URL is set and the ref exists to avoid null errors.
        if (this.state.paymentURL && this.iframeRef.current && this.isIframeReadyToLoad(this.state.paymentURL)) {
            this.loadIframe(this.state.paymentURL)
        }
    }

    private isIframeReadyToLoad(url: string): boolean {
        if (isPaymentListenerUrl(url)) {
            return true
        }

        return resolveSubscriptionKey(this.state.activeSubscriptionKey, '') !== ''
    }

    private async createRegistryAndResolveKey(
        apiData: Record<string, any>,
        logLabel: string
    ): Promise<string | number> {
        const response = await auth.createSubscriptionRegistry({ ...apiData })
        const subscriptionKey = resolveSubscriptionKey(response && response.data ? response.data.id : '', '')
        if (subscriptionKey === '') {
            console.error(`[TranzilaPopup] Missing subscription key in ${logLabel} registry response`, response)
            throw new Error('Missing subscription key in registry response')
        }
        return subscriptionKey
    }

    private async handleFreePurchaseSubmission(subscriptionKey: string | number): Promise<void> {
        // Run the full free-purchase flow inline: mark as purchased, run release hooks, then load listener.
        try {
            this.props.purchaseStore.setClientInfoFetcher(this.props.getClientInfo)
            const freePurchasePayload = buildFreePurchasePayload({
                productDetails: this.props.productDetails,
                effectivePrice: this.state.currentPrice,
                email: this.state.clientDetails.email || this.props.clientInfo.email,
                city: this.state.clientDetails.city || '',
                address: this.state.clientDetails.address || '',
                providedBy:
                    Number(this.props.clientInfo.id) !== Number(this.props.clientInfo.agent_id)
                        ? this.props.clientInfo.agent_id
                        : null,
                clientId: this.props.clientInfo.id,
                subscriptionKey,
                isPrepayment: this.state.isPrepayment,
                prepaymentMonths: this.state.prepaymentMonths
            })
            const purchaseResponse = await auth.setFreeSubAsPurchased(freePurchasePayload)
            const isSuccess = !!(purchaseResponse && purchaseResponse.status === 200)

            if (isSuccess) {
                await this.props.purchaseStore.updateAfterRelease()
            }
            this.setState(
                {
                    paymentURL: `${window.location.origin}/payment-listener/${isSuccess ? 1 : 0}`,
                    tranzilaPaymentStatus: isSuccess,
                    isTranzilaFormSubmitted: true,
                    isPaymentListenerVisible: true,
                    activeSubscriptionKey: subscriptionKey
                },
                () => this.loadPaymentIframeIfReady()
            )
        } catch (error) {
            console.error('[TranzilaPopup] Free purchase submission failed', error)
            alertPopup({
                type: 'error',
                title: 'שגיאה',
                body: 'יצירת רכישה חינמית נכשלה'
            })
        }
    }

    private handlePaidPurchaseSubmission(subscriptionKey: string | number): void {
        this.initPaymentURL()
        this.setState({ activeSubscriptionKey: subscriptionKey }, () => this.loadPaymentIframeIfReady())
    }

    // Add a new blocked row only if the blocked row is for a different product.
    private shouldCreateNewBlockedRow(recentBlockedRow: any): boolean {
        if (!this.props.purchaseStore.getCardCurrentlyBlocked()) {
            return false
        }

        if (!recentBlockedRow) {
            return false
        }

        return this.props.productDetails.id !== recentBlockedRow.subscription_id
    }

    // Render trigger button, modal content, and payment iframe.
    render(): React.ReactNode {
        // Pull frequently-used values for clearer JSX conditions and bindings.
        const {
            popupStore: { popupType },
            productDetails,
            purchaseStore
        } = this.props

        const {
            clientDetails,
            isTranzilaFormSubmitted,
            isPaymentListenerVisible,
            tranzilaPaymentStatus,
            couponCode,
            showCouponError,
            currentPrice,
            priceBeforeDiscount,
            isPrepayment,
            prepaymentMonths,
            isDuplicateCheckLoading
        } = this.state
        // Bind directly to the form state so users can clear inputs; prefill happens at mount time
        // via createClientDetails, so this never shows an empty field on first render.
        const isSubmitDisabled =
            isDuplicateCheckLoading ||
            !purchaseStore.cardStatusResolved ||
            !clientDetails.fullName ||
            !clientDetails.phone ||
            !clientDetails.email ||
            !clientDetails.city ||
            !clientDetails.address ||
            (isPrepayment && prepaymentMonths === null)
        const shouldShowResultState = isTranzilaFormSubmitted || isPaymentListenerVisible
        // Collective flow: no charge — hide coupon/prepayment and use a details-only title.
        const collectiveMode = purchaseStore.collectiveMode

        return (
            <>
                <style>{styles}</style>
                <Modal
                    width={'850px'}
                    onCancel={this.onClose}
                    afterClose={this.handleAfterClose}
                    visible={popupType === POPUP_TYPE}
                    destroyOnClose={false}
                    maskClosable={false}
                    keyboard={false}
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{HEBREW_TEXTS.buttonTitle}</span>
                            {!shouldShowResultState && (
                                <Button size="small" onClick={this.goBackToPurchase}>
                                    {HEBREW_TEXTS.backToPurchase}
                                </Button>
                            )}
                        </div>
                    }
                    footer={null}
                >
                    <div style={{ width: '100%', height: '100%' }}>
                        <span
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '24px',
                                fontWeight: 'bold',
                                marginBottom: '10px'
                            }}
                        >
                            {collectiveMode ? HEBREW_TEXTS.detailsTitle : HEBREW_TEXTS.securePaymentTitle}
                        </span>
                        <span
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '18px',
                                marginBottom: '10px'
                            }}
                        >
                            <span style={{ color: '#000' }}>{productDetails ? productDetails.name : ''}</span>
                            <span style={{ marginInline: '6px', color: '#000' }}>{HEBREW_TEXTS.separator}</span>
                            {collectiveMode ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {this.state.defaultProductPrice > 0 && (
                                        <span style={{ color: '#999', textDecoration: 'line-through' }}>
                                            {Number(this.state.defaultProductPrice.toFixed(2))} {HEBREW_TEXTS.shekel}
                                        </span>
                                    )}
                                    <span style={{ color: '#3ec8d5' }}>0 {HEBREW_TEXTS.shekel}</span>
                                </span>
                            ) : !shouldShowResultState && isPrepayment && prepaymentMonths ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {priceBeforeDiscount > 0 && (
                                        <span
                                            style={{ color: '#999', textDecoration: 'line-through', fontSize: '16px' }}
                                        >
                                            {Number((priceBeforeDiscount * prepaymentMonths).toFixed(2))}{' '}
                                            {HEBREW_TEXTS.shekel}
                                        </span>
                                    )}
                                    <span style={{ color: '#3ec8d5', fontSize: '16px' }}>
                                        {Number(currentPrice.toFixed(2))} {HEBREW_TEXTS.shekel} / חודש ×{' '}
                                        {prepaymentMonths} חודשים ={' '}
                                        {Number((currentPrice * prepaymentMonths).toFixed(2))} {HEBREW_TEXTS.shekel}
                                    </span>
                                </span>
                            ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {priceBeforeDiscount > 0 && (
                                        <span style={{ color: '#999', textDecoration: 'line-through' }}>
                                            {Number(priceBeforeDiscount.toFixed(2))} {HEBREW_TEXTS.shekel}
                                        </span>
                                    )}
                                    <span style={{ color: '#3ec8d5' }}>
                                        {isPrepayment && prepaymentMonths
                                            ? Number((currentPrice * prepaymentMonths).toFixed(2))
                                            : Number(currentPrice.toFixed(2))}{' '}
                                        {HEBREW_TEXTS.shekel}
                                    </span>
                                </span>
                            )}
                        </span>

                        {!shouldShowResultState && (
                            <span
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    marginBottom: '20px'
                                }}
                            >
                                <span
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '18px',
                                        marginBottom: '10px',
                                        flexDirection: 'column',
                                        width: '65%'
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '10px',
                                            width: '100%'
                                        }}
                                    >
                                        <Input
                                            placeholder={HEBREW_TEXTS.fullName}
                                            autoComplete="off"
                                            value={clientDetails.fullName}
                                            name="fullName"
                                            onChange={this.createSanitizedInputHandler(sanitizeFullName, 'fullName')}
                                        />
                                        <Input
                                            placeholder={HEBREW_TEXTS.phone}
                                            autoComplete="off"
                                            value={clientDetails.phone}
                                            name="phone"
                                            onChange={this.createSanitizedInputHandler(sanitizePhone, 'phone')}
                                        />
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '10px',
                                            width: '100%'
                                        }}
                                    >
                                        <Input
                                            placeholder={HEBREW_TEXTS.email}
                                            autoComplete="off"
                                            value={clientDetails.email}
                                            name="email"
                                            onChange={event =>
                                                this.setValue(sanitizeEmail(event.target.value), 'email')
                                            }
                                        />
                                        <Input
                                            placeholder={HEBREW_TEXTS.address}
                                            autoComplete="off"
                                            value={clientDetails.address}
                                            name="address"
                                            onChange={event =>
                                                this.setValue(sanitizeAddress(event.target.value), 'address')
                                            }
                                        />
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '10px',
                                            width: '100%'
                                        }}
                                    >
                                        <Input
                                            placeholder={HEBREW_TEXTS.city}
                                            autoComplete="off"
                                            value={clientDetails.city}
                                            name="city"
                                            onChange={this.createSanitizedInputHandler(sanitizeNoNumbers, 'city')}
                                        />
                                        <Input
                                            placeholder={HEBREW_TEXTS.company}
                                            autoComplete="off"
                                            value={clientDetails.company}
                                            name="company"
                                            onChange={event =>
                                                this.setValue(sanitizeNoNumbers(event.target.value), 'company')
                                            }
                                        />
                                    </span>

                                    {!collectiveMode && (
                                        <>
                                            <div style={{ width: '100%', height: '57px' }}>
                                                <Tooltip>
                                                    <Input
                                                        placeholder={HEBREW_TEXTS.couponPlaceholder}
                                                        autoComplete="off"
                                                        value={couponCode}
                                                        name="couponCode"
                                                        onChange={this.handleCouponInput}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Tooltip>
                                                {showCouponError && (
                                                    <div style={{ color: 'red', marginTop: '0px', fontSize: '12px' }}>
                                                        {showCouponError}
                                                    </div>
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '40px',
                                                    marginTop: '5px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={this.state.isPrepayment}
                                                    onChange={e => this.handlePrepaymentToggle(e.target.checked)}
                                                    style={{ margin: 0 }}
                                                >
                                                    {HEBREW_TEXTS.prepayment}
                                                </Checkbox>
                                                {this.state.isPrepayment && (
                                                    <Select
                                                        className="prepayment-select"
                                                        value={
                                                            this.state.prepaymentMonths === null
                                                                ? undefined
                                                                : this.state.prepaymentMonths
                                                        }
                                                        onChange={value => this.setState({ prepaymentMonths: value })}
                                                        style={{ width: '150px', direction: 'rtl', textAlign: 'right' }}
                                                        placeholder={HEBREW_TEXTS.prepaymentMonths}
                                                    >
                                                        {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                                                            <Select.Option
                                                                key={month}
                                                                value={month}
                                                                style={{ direction: 'rtl' }}
                                                            >
                                                                {month} חודשים
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <Button
                                        style={{ marginTop: '10px' }}
                                        onClick={this.submitTranzilaForm}
                                        disabled={isSubmitDisabled}
                                        loading={!purchaseStore.cardStatusResolved || isDuplicateCheckLoading}
                                    >
                                        {HEBREW_TEXTS.submit}
                                    </Button>
                                </span>
                            </span>
                        )}
                        {shouldShowResultState && (
                            <>
                                {isPaymentListenerVisible ? (
                                    <div
                                        style={{
                                            width: '100%',
                                            minHeight: '200px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <PaymentListenerContent
                                            status={tranzilaPaymentStatus === null ? null : tranzilaPaymentStatus}
                                        />
                                    </div>
                                ) : (
                                    <iframe
                                        id="my-iframe"
                                        name="tranzila-iframe-target"
                                        ref={this.handleIframeRef}
                                        onLoad={this.handleIframeLoad}
                                        style={{
                                            width: '100%',
                                            height: '410px',
                                            border: 'none'
                                        }}
                                        title="Tranzila"
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Modal>
            </>
        )
    }
}

export default withRouter(TranzilaPopup) as any
